import type { CommunicationPreferencesSettingsDto } from '@platform/api';
import type { useTrack } from '@platform/tracking';
import { type EventMappers, registerGa4Events } from '../../tracking/dataLayerAdapter';
import { diffToggleEvents, type FormValues } from './notifications.utils';
import type { NotificationChannel } from './preference-matrix.constants';

type Track = ReturnType<typeof useTrack>;

export type NotificationCategory = keyof CommunicationPreferencesSettingsDto;

declare module '@platform/tracking' {
  interface TrackingEvents {
    notification_settings_viewed: {
      communication_fields: [];
    };

    notification_settings_category_toggled: {
      communication_fields: Array<{
        channel: NotificationChannel;
        category: NotificationCategory;
        enabled: boolean;
      }>;
    };

    /** Covers both a single channel and every channel flipping uniformly — one entry per channel. */
    notification_settings_master_toggled: {
      communication_fields: Array<{ channel: NotificationChannel; enabled: boolean }>;
    };

    notification_settings_save_completed: {
      communication_fields: [];
    };

    notification_settings_save_failed: {
      communication_fields: [];
    };

    notification_unsubscribe_completed: { campaign: string };

    notification_unsubscribe_failed: { campaign: string };
  }
}

/** Fires `notification_settings_save_completed` plus a `master_toggled`/`category_toggled` event per changed channel/category. */
export function trackNotificationSettingsSaved(
  track: Track,
  before: FormValues,
  after: FormValues,
  channels: readonly NotificationChannel[],
) {
  track('notification_settings_save_completed', { communication_fields: [] });

  const { toggled, masterToggled } = diffToggleEvents(before, after, channels);
  if (masterToggled.length > 0) {
    track('notification_settings_master_toggled', { communication_fields: masterToggled });
  }
  if (toggled.length > 0) {
    track('notification_settings_category_toggled', { communication_fields: toggled });
  }
}

export function trackNotificationSettingsSaveFailed(track: Track) {
  track('notification_settings_save_failed', { communication_fields: [] });
}

// Kept on the existing `device-privacy` GTM trigger (see account/login tracking.ts for the pattern).
export const notificationsGa4EventMappers: EventMappers = {
  notification_settings_viewed: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_settings_viewed',
    communication_fields: p.communication_fields,
  }),
  notification_settings_category_toggled: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_settings_category_toggled',
    communication_fields: p.communication_fields,
  }),
  notification_settings_master_toggled: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_settings_master_toggled',
    communication_fields: p.communication_fields,
  }),
  notification_settings_save_completed: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_settings_save_completed',
    communication_fields: p.communication_fields,
  }),
  notification_settings_save_failed: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_settings_save_failed',
    communication_fields: p.communication_fields,
  }),
  notification_unsubscribe_completed: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_unsubscribe_completed',
    campaign: p.campaign,
  }),
  notification_unsubscribe_failed: (p) => ({
    event: 'device-privacy',
    event_description: 'notification_unsubscribe_failed',
    campaign: p.campaign,
  }),
};

registerGa4Events(notificationsGa4EventMappers);
